package features.auth

import features.BaseViewModel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import features.booking.UserRepository

class AuthViewModel(private val repository: UserRepository) : BaseViewModel() {
    val email = MutableStateFlow("")
    val password = MutableStateFlow("")

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    private val _errorMessage = MutableStateFlow<String?>(null)
    val errorMessage: StateFlow<String?> = _errorMessage.asStateFlow()

    private val _roleAssigned = MutableStateFlow<String?>(null)
    val roleAssigned: StateFlow<String?> = _roleAssigned.asStateFlow()
    val nombreUsuario = MutableStateFlow("")

    private val _requiereCambio = MutableStateFlow(false)
    val requiereCambio: StateFlow<Boolean> = _requiereCambio.asStateFlow()

    private val _cambioExitoso = MutableStateFlow(false)
    val cambioExitoso: StateFlow<Boolean> = _cambioExitoso.asStateFlow()

    private val _cambioError = MutableStateFlow<String?>(null)
    val cambioError: StateFlow<String?> = _cambioError.asStateFlow()

    private var _uidActual: String = ""

    fun login() {
        if (email.value.isBlank() || password.value.isBlank()) {
            _errorMessage.value = "Por favor, llena todos los campos."
            return
        }

        viewModelScope.launch {
            _isLoading.value = true
            _errorMessage.value = null

            try {
                val uid = repository.signIn(email.value, password.value)
                _uidActual = uid

                // Check if user must change password
                val debeCambiar = repository.requiereCambioContrasena(uid)
                if (debeCambiar) {
                    _requiereCambio.value = true
                    _isLoading.value = false
                    return@launch
                }

                val userRole = repository.getUserRole(uid)
                // Solo staff interno accede vía login: chofer/chofer_admin/admin+/desarrollador.
                // Cliente ya no existe como rol; los compradores usan Anonymous Auth sin login.
                val rolesInternos = setOf("chofer", "chofer_admin", "admin", "admin_general", "master", "desarrollador")
                if (userRole !in rolesInternos) {
                    _errorMessage.value = "Esta cuenta no tiene acceso al panel interno."
                    try { network.FirebaseClient.auth.signOut() } catch (_: Exception) {}
                    // Devolver a modo anónimo para que la app siga funcionando.
                    try { network.FirebaseClient.auth.signInAnonymously() } catch (_: Exception) {}
                    _isLoading.value = false
                    return@launch
                }
                _roleAssigned.value = userRole
                val nombreDelUsuario = repository.getUserName(uid)
                nombreUsuario.value = nombreDelUsuario ?: email.value

                // Asociar reportes de error a este usuario
                network.CrashReporter.setUserId(uid)
                network.CrashReporter.setUserContext(email.value, userRole ?: "")

                // Limpiar password de memoria después de login exitoso
                password.value = ""

                // Registrar token FCM para push notifications
                org.example.abigail.registrarTokenPush(uid)
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "AuthViewModel.login")
                val msg = e.message?.lowercase() ?: ""
                _errorMessage.value = when {
                    msg.contains("password") || msg.contains("credential") || msg.contains("user-not-found") || msg.contains("wrong-password") ->
                        "Correo o contraseña incorrectos."
                    msg.contains("network") || msg.contains("timeout") || msg.contains("connect") ->
                        "Error de conexión. Verifica tu internet."
                    msg.contains("too-many-requests") ->
                        "Demasiados intentos. Espera un momento antes de reintentar."
                    msg.contains("disabled") ->
                        "Esta cuenta ha sido deshabilitada."
                    else -> "Error al iniciar sesión. Intenta de nuevo."
                }
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun cambiarContrasena(nueva: String) {
        viewModelScope.launch {
            _isLoading.value = true
            _cambioError.value = null
            // Fortaleza mínima: 8+ chars, al menos una mayúscula y un dígito.
            // Firebase Auth acepta desde 6 chars pero dejamos '12345678' fuera aquí.
            if (nueva.length < 8 || nueva.none { it.isUpperCase() } || nueva.none { it.isDigit() }) {
                _cambioError.value = "Mínimo 8 caracteres con al menos 1 mayúscula y 1 número."
                _isLoading.value = false
                return@launch
            }
            try {
                val exito = repository.cambiarContrasena(nueva)
                if (exito) {
                    repository.limpiarFlagCambio(_uidActual)
                    _requiereCambio.value = false
                    _cambioExitoso.value = true
                    password.value = ""

                    // Continue login flow
                    val userRole = repository.getUserRole(_uidActual)
                    _roleAssigned.value = userRole
                    val nombreDelUsuario = repository.getUserName(_uidActual)
                    nombreUsuario.value = nombreDelUsuario ?: email.value
                } else {
                    _cambioError.value = "No se pudo cambiar la contraseña. Intenta de nuevo."
                }
            } catch (e: Exception) {
                _cambioError.value = "Error: ${e.message}"
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun limpiarSesion() {
        // El modo prueba queda guardado en el servidor, pero no debe seguir activo en memoria
        // para el siguiente usuario que entre en este dispositivo.
        network.ModoPruebaPagos.limpiarSesion()
        email.value = ""
        password.value = ""
        nombreUsuario.value = ""
        _roleAssigned.value = null
        _errorMessage.value = null
        _requiereCambio.value = false
        _cambioExitoso.value = false
        _cambioError.value = null
        _uidActual = ""
    }
}
